import React from 'react'
import { motion } from "framer-motion"
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'

export const Privacy = ({ language }) => {
    const isAr = language === 'ar'

    const fadeUp = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }
    }

    const sections = [
        { id: 'definitions', title: isAr ? '1. التفسير والتعريفات' : '1. Definitions', icon: 'lucide:book-open' },
        { id: 'collection', title: isAr ? '2. البيانات التي نجمعها' : '2. Data Collection', icon: 'lucide:database' },
        { id: 'cookies', title: isAr ? '3. ملفات تعريف الارتباط (Cookies)' : '3. Tracking & Cookies', icon: 'lucide:cookie' },
        { id: 'usage', title: isAr ? '4. كيفية استخدام البيانات' : '4. How We Use Data', icon: 'lucide:cpu' },
        { id: 'sharing', title: isAr ? '5. مشاركة البيانات' : '5. Data Sharing', icon: 'lucide:share-2' },
        { id: 'retention', title: isAr ? '6. الاحتفاظ بالبيانات ونقلها' : '6. Retention & Transfer', icon: 'lucide:archive' },
        { id: 'rights', title: isAr ? '7. حقوقك وحذف البيانات' : '7. Your Rights & Deletion', icon: 'lucide:user-check' },
        { id: 'security', title: isAr ? '8. أمان البيانات' : '8. Security Measures', icon: 'lucide:shield-check' },
        { id: 'children', title: isAr ? '9. خصوصية الأطفال' : '9. Children\'s Privacy', icon: 'lucide:users' },
        { id: 'contact', title: isAr ? '10. معلومات التواصل' : '10. Contact Us', icon: 'lucide:mail' }
    ]

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full min-h-screen bg-[#f8fafc] dark:bg-[#07070f] text-slate-800 dark:text-gray-200 transition-colors duration-300"
        >
            {/* ── HERO HEADER ────────────────────────────────────────── */}
            <div className="relative overflow-hidden border-b border-slate-200 dark:border-white/10"
                style={{
                    background: 'linear-gradient(180deg, rgba(16,185,129,0.08) 0%, rgba(0,210,255,0.03) 100%)'
                }}>
                <div className="absolute top-0 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 pointer-events-none"
                    style={{ background: 'radial-gradient(circle, #10b981, #00d2ff)' }} />

                <div className="max-w-6xl mx-auto px-4 md:px-10 lg:px-20 py-12 md:py-16 relative z-10">
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-6">
                        <Link to="/" className="hover:text-emerald-500 transition-colors flex items-center gap-1">
                            <Icon icon="lucide:arrow-left" className={isAr ? "rotate-180" : ""} />
                            {isAr ? 'الرئيسية' : 'Home'}
                        </Link>
                        <span>/</span>
                        <span className="text-emerald-500">{isAr ? 'سياسة الخصوصية' : 'Privacy Policy'}</span>
                    </div>

                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                        <Icon icon="lucide:shield-check" />
                        {isAr ? 'حماية البيانات والخصوصية' : 'Privacy & Data Protection'}
                    </div>

                    <h1 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tight mb-4">
                        {isAr ? 'سياسة الخصوصية' : 'Privacy Policy'}
                    </h1>

                    <p className="text-sm md:text-base text-slate-600 dark:text-gray-400 max-w-2xl leading-relaxed">
                        {isAr
                            ? 'نحن نولي أهمية قصوى لخصوصيتك وأمان بياناتك. توضح هذه الوثيقة بوضوح كيف يقوم Torky Studio بجمع معلوماتك وحمايتها واستخدامها.'
                            : 'We value your trust and are dedicated to safeguarding your personal data. This policy outlines how Torky Studio collects, uses, and protects your information.'}
                    </p>

                    <div className="flex items-center gap-4 mt-6 text-xs text-slate-500 dark:text-gray-500 font-mono">
                        <span className="flex items-center gap-1">
                            <Icon icon="lucide:clock" /> {isAr ? 'آخر تحديث: يناير 2025' : 'Last updated: January 2025'}
                        </span>
                        <span>•</span>
                        <span>Torky Studio · Mohamed Torky</span>
                    </div>
                </div>
            </div>

            {/* ── CONTENT GRID ───────────────────────────────────────── */}
            <div className="max-w-6xl mx-auto px-4 md:px-10 lg:px-20 py-12 md:py-16">
                <div className="flex flex-col lg:flex-row gap-10">

                    {/* Table of Contents Sticky Sidebar (Desktop) */}
                    <aside className="lg:w-64 flex-shrink-0">
                        <div className="sticky top-28 p-5 rounded-2xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <h2 className="text-xs font-black uppercase tracking-widest text-slate-400 dark:text-gray-500 mb-4 font-mono">
                                {isAr ? '// الفهرس' : '// On this page'}
                            </h2>
                            <nav className="space-y-1">
                                {sections.map(s => (
                                    <a
                                        key={s.id}
                                        href={`#${s.id}`}
                                        className="flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-xl text-slate-600 dark:text-gray-400 hover:text-emerald-500 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                                    >
                                        <Icon icon={s.icon} className="text-sm opacity-60 flex-shrink-0" />
                                        <span className="truncate">{s.title}</span>
                                    </a>
                                ))}
                            </nav>
                        </div>
                    </aside>

                    {/* Main Document Body */}
                    <main className="flex-1 space-y-8 text-start">

                        {/* Section 1: Definitions */}
                        <motion.section id="definitions" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-6 md:p-8 rounded-3xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <div className="flex items-center gap-2 text-emerald-500 mb-3">
                                <Icon icon="lucide:book-open" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '1. التفسير والتعريفات' : '1. Definitions & Scope'}
                                </h2>
                            </div>
                            <p className="text-sm leading-relaxed text-slate-600 dark:text-gray-400 mb-5">
                                {isAr
                                    ? 'تحدد الكلمات الآتية المفاهيم الأساسية الواردة في سياسة الخصوصية هذه:'
                                    : 'For the purposes of this Privacy Policy, the following terms carry specific meanings:'}
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                {[
                                    { label: isAr ? 'البيانات الشخصية (Personal Data)' : 'Personal Data', desc: isAr ? 'أي معلومات تتعلق بفرد محدد أو يمكن التعرف عليه مباشرة أو غير مباشرة.' : 'Any information relating to an identified or identifiable individual.' },
                                    { label: isAr ? 'بيانات الاستخدام (Usage Data)' : 'Usage Data', desc: isAr ? 'بيانات يتم جمعها تلقائيًا ناتجة عن زيارة وتصفح الموقع.' : 'Data collected automatically, such as page visits and duration.' },
                                    { label: isAr ? 'ملفات تعريف الارتباط (Cookies)' : 'Cookies', desc: isAr ? 'ملفات صغيرة تُحفظ على جهازك لتخصيص التفضيلات وتحسين الأداء.' : 'Small files placed on your device to remember preferences and optimize performance.' },
                                    { label: isAr ? 'مقدم الخدمة (Service Provider)' : 'Service Provider', desc: isAr ? 'الجهات أو الأدوات التي تساعدنا في تشغيل الموقع مثل منصات الاستضافة والبريد.' : 'Third parties employed to facilitate or analyze the Service.' },
                                ].map((item, idx) => (
                                    <div key={idx} className="p-3.5 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/5">
                                        <div className="font-bold text-slate-900 dark:text-white mb-1">{item.label}</div>
                                        <div className="text-slate-600 dark:text-gray-400 leading-relaxed">{item.desc}</div>
                                    </div>
                                ))}
                            </div>
                        </motion.section>

                        {/* Section 2: Data Collection */}
                        <motion.section id="collection" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-6 md:p-8 rounded-3xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <div className="flex items-center gap-2 text-emerald-500 mb-3">
                                <Icon icon="lucide:database" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '2. أنواع البيانات التي نجمعها' : '2. Types of Data Collected'}
                                </h2>
                            </div>
                            <div className="space-y-4 text-sm leading-relaxed text-slate-600 dark:text-gray-400">
                                <div>
                                    <h3 className="font-bold text-slate-900 dark:text-white mb-1">
                                        {isAr ? '• البيانات المقدمة طواعية' : '• Personally Identifiable Information'}
                                    </h3>
                                    <p>
                                        {isAr
                                            ? 'عند استخدام نموذج طلب الخدمة أو التواصل معنا عبر البريد أو الواتساب، قد يُطلب منك تقديم معلومات مثل: الاسم، عنوان البريد الإلكتروني، رقم الهاتف، والملاحظات الخاصة بمشروعك.'
                                            : 'When submitting an inquiry, booking a service, or contacting us directly, you may provide your name, email address, WhatsApp/phone number, and project requirements.'}
                                    </p>
                                </div>
                                <div>
                                    <h3 className="font-bold text-slate-900 dark:text-white mb-1">
                                        {isAr ? '• بيانات التصفح التلقائية' : '• Automatically Collected Usage Data'}
                                    </h3>
                                    <p>
                                        {isAr
                                            ? 'قد نقوم بجمع معلومات تلقائية مثل نوع المتصفح، دقة الشاشة، الصفحات التي زرتها، والوقت المقضي على الموقع لغرض تحسين تجربة التصفح وحماية أمان الموقع.'
                                            : 'We may collect information that your browser sends automatically, such as IP address, browser type and version, pages visited, time spent, and general diagnostic metrics.'}
                                    </p>
                                </div>
                            </div>
                        </motion.section>

                        {/* Section 3: Cookies */}
                        <motion.section id="cookies" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-6 md:p-8 rounded-3xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <div className="flex items-center gap-2 text-emerald-500 mb-3">
                                <Icon icon="lucide:cookie" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '3. ملفات تعريف الارتباط وتقنيات التتبع' : '3. Tracking Technologies & Cookies'}
                                </h2>
                            </div>
                            <div className="space-y-3 text-sm leading-relaxed text-slate-600 dark:text-gray-400">
                                <p>
                                    {isAr
                                        ? 'نستخدم ملفات تعريف الارتباط الأساسية لحفظ تفضيلاتك، مثل اللغة المفضلة (العربية أو الإنجليزية) ووضع العرض (الوضع الليلي أو الفاتح) حتى لا تضطر إلى إعادة ضبطها في كل زيارة.'
                                        : 'We use essential and functional cookies and local storage to store your user preferences—such as language selection (English or Arabic) and dark/light mode preference.'}
                                </p>
                                <p>
                                    {isAr
                                        ? 'يمكنك توجيه متصفحك لرفض جميع ملفات تعريف الارتباط، إلا أن بعض ميزات التخصيص قد لا تعمل بالشكل الأمثل.'
                                        : 'You can configure your browser to refuse all cookies. Note that doing so may affect your customized experience on the site.'}
                                </p>
                            </div>
                        </motion.section>

                        {/* Section 4: Data Usage */}
                        <motion.section id="usage" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-6 md:p-8 rounded-3xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <div className="flex items-center gap-2 text-emerald-500 mb-3">
                                <Icon icon="lucide:cpu" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '4. كيف نستخدم بياناتك الشخصية' : '4. How We Use Your Data'}
                                </h2>
                            </div>
                            <ul className="space-y-2 text-sm text-slate-600 dark:text-gray-400">
                                {[
                                    isAr ? 'تقديم وإدارة الخدمات البرمجية والتصميمية التي تطلبها من Torky Studio.' : 'To deliver, build, and maintain the development and design services you contract.',
                                    isAr ? 'التواصل معك بشأن استفساراتك وعروض الأسعار وتحديثات المشاريع.' : 'To respond to your inquiries, send quotes, and provide project milestone updates.',
                                    isAr ? 'تحسين كفاءة وسرعة الموقع وتقديم تجربة مستخدم سلسة.' : 'To optimize performance, security, and the overall digital experience of the website.',
                                    isAr ? 'الامتثال للالتزامات القانونية وحماية حقوق الملكية الفكرية.' : 'To fulfill our legal obligations and protect our intellectual property rights.',
                                ].map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-2">
                                        <Icon icon="lucide:check" className="text-emerald-500 text-sm mt-1 flex-shrink-0" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.section>

                        {/* Section 5: Data Sharing */}
                        <motion.section id="sharing" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-6 md:p-8 rounded-3xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <div className="flex items-center gap-2 text-emerald-500 mb-3">
                                <Icon icon="lucide:share-2" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '5. مشاركة البيانات والإفصاح' : '5. Data Sharing & Disclosure'}
                                </h2>
                            </div>
                            <p className="text-sm leading-relaxed text-slate-600 dark:text-gray-400">
                                {isAr
                                    ? 'نحن لا نبيع أو نؤجر أو نتاجر ببياناتك الشخصية لأي طرف ثالث على الإطلاق. قد نشارك البيانات فقط مع مقدمي الخدمات الموثوقين لمعالجة الطلبات، أو إذا كان ذلك مطلوبًا بموجب القانون لحماية حقوقنا القانونية.'
                                    : 'We never sell, rent, or trade your personal information to third parties. We only share necessary data with trusted infrastructure providers (e.g. hosting, email delivery) or when required by law to protect our legal rights.'}
                            </p>
                        </motion.section>

                        {/* Section 6: Retention & Security */}
                        <motion.section id="security" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-6 md:p-8 rounded-3xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <div className="flex items-center gap-2 text-emerald-500 mb-3">
                                <Icon icon="lucide:shield-check" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '6. أمان البيانات والاحتفاظ بها' : '6. Data Security & Retention'}
                                </h2>
                            </div>
                            <p className="text-sm leading-relaxed text-slate-600 dark:text-gray-400">
                                {isAr
                                    ? 'نحتفظ ببياناتك فقط للمدة اللازمة لتحقيق الأغراض المنصوص عليها في هذه السياسة وتنفيذ العقود المبرمة. نطبق بروتوكولات حماية متطورة وتشفير SSL لحماية نقل بياناتك عبر الإنترنت.'
                                    : 'We retain personal data only for as long as necessary to fulfill project requirements and comply with legal requirements. We utilize modern SSL encryption and industry-standard security safeguards to protect your data.'}
                            </p>
                        </motion.section>

                        {/* Section 7: Rights & Deletion */}
                        <motion.section id="rights" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-6 md:p-8 rounded-3xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <div className="flex items-center gap-2 text-emerald-500 mb-3">
                                <Icon icon="lucide:user-check" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '7. حقوقك وحذف البيانات' : '7. Your Rights & Data Deletion'}
                                </h2>
                            </div>
                            <p className="text-sm leading-relaxed text-slate-600 dark:text-gray-400">
                                {isAr
                                    ? 'يحق لك في أي وقت طلب مراجعة بياناتك الشخصية المخزنة لدينا، أو تصحيحها، أو طلب حذفها نهائيًا. يمكنك إرسال طلب الحذف عبر البريد الإلكتروني وسنقوم بالاستجابة في غضون أيام عمل قليلة.'
                                    : 'You have the right to request access to, correction of, or permanent deletion of the personal data we hold about you. Simply email us with your request and we will process it promptly.'}
                            </p>
                        </motion.section>

                        {/* Section 8: Children */}
                        <motion.section id="children" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-6 md:p-8 rounded-3xl bg-white dark:bg-[#0c0d1b] border border-slate-200 dark:border-white/10 shadow-sm">
                            <div className="flex items-center gap-2 text-emerald-500 mb-3">
                                <Icon icon="lucide:users" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '8. خصوصية الأطفال والتعديلات' : '8. Children\'s Privacy & Policy Updates'}
                                </h2>
                            </div>
                            <p className="text-sm leading-relaxed text-slate-600 dark:text-gray-400">
                                {isAr
                                    ? 'خدماتنا غير موجهة لمن هم دون سن 13 عامًا. يجوز لنا تحديث سياسة الخصوصية هذه وسنقوم بنشر السياسة المحدثة مع تعديل تاريخ آخر تحديث.'
                                    : 'Our service does not address individuals under the age of 13. We may update this Privacy Policy periodically and will notify users by posting the updated terms on this page.'}
                            </p>
                        </motion.section>

                        {/* Section 9: Contact Us */}
                        <motion.section id="contact" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                            className="p-8 rounded-3xl bg-gradient-to-br from-emerald-500/10 via-cyan-500/5 to-transparent border border-emerald-500/20 shadow-sm">
                            <div className="flex items-center gap-2 text-emerald-500 mb-3">
                                <Icon icon="lucide:mail" className="text-lg" />
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isAr ? '9. للتواصل والاستفسارات' : '9. Privacy Contact'}
                                </h2>
                            </div>
                            <p className="text-sm text-slate-600 dark:text-gray-400 mb-6">
                                {isAr
                                    ? 'إذا كانت لديك أي أسئلة أو طلبات تتعلق بخصوصيتك وبياناتك الشخصية، يرجى التواصل معنا مباشرة:'
                                    : 'For any inquiries or data requests regarding this Privacy Policy, please contact Mohamed Torky directly:'}
                            </p>

                            <div className="flex flex-wrap gap-4">
                                <a
                                    href="mailto:mohamed2003torky@gmail.com"
                                    className="flex items-center gap-2 px-5 py-3 rounded-2xl bg-white dark:bg-white/10 border border-slate-200 dark:border-white/10 text-xs font-bold hover:border-emerald-500 transition-colors"
                                >
                                    <Icon icon="mdi:email" className="text-base text-red-500" />
                                    <span>mohamed2003torky@gmail.com</span>
                                </a>
                                <a
                                    href="http://wa.me/201554206775"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-2 px-5 py-3 rounded-2xl bg-white dark:bg-white/10 border border-slate-200 dark:border-white/10 text-xs font-bold hover:border-emerald-500 transition-colors"
                                >
                                    <Icon icon="ic:baseline-whatsapp" className="text-base text-emerald-500" />
                                    <span>+20 155 420 6775</span>
                                </a>
                            </div>
                        </motion.section>

                    </main>
                </div>
            </div>
        </motion.div>
    )
}
